import React, { useState, useEffect, useRef, useCallback } from "react";
import CreatableSelect from "react-select/creatable";
import CandidateTableBody from "./CandidateTableBody";
import { renderCustomPagination } from "./candidateHelpers";

const selectStyles = {
    control: (base) => ({
        ...base,
        minWidth: 180,
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: '"Inter", sans-serif',
        borderColor: "#E4E9F0",
        boxShadow: "none",
        "&:hover": { borderColor: "#C4CAD3" },
    }),
    menu: (base) => ({ ...base, minWidth: 180, fontFamily: '"Inter", sans-serif', fontSize: "13px" }),
    menuList: (base) => ({ ...base, overflowX: "hidden" }),
    placeholder: (base) => ({ ...base, color: "#8A9199", fontFamily: '"Inter", sans-serif' }),
    option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif', fontSize: "13px" }),
    singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
};

const AllCandidatesTab = ({
    candidates,
    loading,
    onViewSummary,
    onDownload,
    onAddClick,
    folderOptions,
    folderLoading,
    listOptions,
    listLoading,
    onFilterChange,
    pagination,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        setSelectedList(null);
    }, [selectedFolder]);

    const triggerFilter = useCallback(
        (search, folder_id, list_id, limit = pageSize, page = 1) => {
            if (onFilterChange) {
                onFilterChange({ search, folder_id, list_id, limit, page });
            }
        },
        [onFilterChange, pageSize]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            triggerFilter(value, selectedFolder?.value, selectedList?.value, pageSize, 1);
        }, 500);
    };

    const handleFolderChange = (option) => {
        setSelectedFolder(option);
        setSelectedList(null);
        triggerFilter(searchText, option?.value, null, pageSize, 1);
    };

    const handleListChange = (option) => {
        setSelectedList(option);
        triggerFilter(searchText, selectedFolder?.value, option?.value, pageSize, 1);
    };

    const total = pagination?.total || candidates.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);

    return (
        <div className="cv-tab-body">
            <div className="cv-controls-bar">
                <div className="cv-search-wrap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="cv-search-icon">
                        <circle cx="11" cy="11" r="8" stroke="#8A9199" strokeWidth="2" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#8A9199" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        className="cv-search-input"
                        placeholder="Search candidates..."
                        value={searchText}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="cv-filter-group">
                    <CreatableSelect
                        inputId="all-candidates-folder-filter"
                        value={selectedFolder}
                        onChange={handleFolderChange}
                        options={folderOptions || []}
                        isLoading={folderLoading}
                        isDisabled={folderLoading}
                        isClearable
                        placeholder="All Folders"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                    <CreatableSelect
                        inputId="all-candidates-list-filter"
                        value={selectedList}
                        onChange={handleListChange}
                        options={(listOptions || []).map((l) => ({ value: l.id, label: l.name }))}
                        isLoading={listLoading}
                        isDisabled={listLoading}
                        isClearable
                        placeholder="All Lists"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                </div>
            </div>

            <div className="cv-table-outer">
                <div className="cv-table-scroll">
                    <table className="cv-table">
                        <thead>
                            <tr>
                                <th className="th-name">Name</th>
                                <th className="th-center">Email</th>
                                <th className="th-center">Designation</th>
                                <th className="th-center">Added On</th>
                                <th className="th-skills">Skills</th>
                                <th className="th-center">List Name</th>
                                <th className="th-actions">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="td-loading">
                                        <div className="spinner-border cv-spinner" role="status">
                                            <span className="visually-hidden">Loading candidates...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <CandidateTableBody
                                    candidates={candidates}
                                    onViewSummary={onViewSummary}
                                    onDownload={onDownload}
                                    onAddClick={onAddClick}
                                />
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && total > 0 && (
                    <div className="cv-pagination">
                        <span className="cv-pagination-info">
                            Showing {start}–{end} of <strong>{total}</strong> Candidates
                        </span>
                        <div className="cv-pagination-right">
                            <div className="cv-page-btns">
                                {renderCustomPagination(totalPages, currentPage, (p) => {
                                    setCurrentPage(p);
                                    triggerFilter(searchText, selectedFolder?.value, selectedList?.value, pageSize, p);
                                })}
                            </div>
                            <div className="cv-per-page">
                                <span>Per page:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        const newSize = parseInt(e.target.value);
                                        setPageSize(newSize);
                                        setCurrentPage(1);
                                        triggerFilter(searchText, selectedFolder?.value, selectedList?.value, newSize, 1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllCandidatesTab;
